import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { ProfileContextType, UserProfile } from '@typeDefs/domain/profile';
import { useAuth } from '@contexts/auth/AuthContext';
import { userService } from '@services/api';

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();

  const [profileData, setProfileData] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentBusinessId, setCurrentBusinessId] = useState<string | null>(null);

  const availableBusinesses = useMemo(() => profileData?.corporateEntities || [], [profileData]);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const switchBusiness = useCallback(
    async (businessId: string) => {
      if (!availableBusinesses.find((b) => b.id === businessId)) {
        throw new Error('Access denied to this business');
      }

      setCurrentBusinessId(businessId);
      // Persist selected business to localStorage
      sessionStorage.setItem('selectedBusinessId', businessId);
    },
    [availableBusinesses],
  );

  const clearProfile = useCallback(() => {
    setProfileData(null);
    setCurrentBusinessId(null);
    setError(null);
    // Clear welcome session flag and business selection on logout
    sessionStorage.removeItem('welcome-shown');
    sessionStorage.removeItem('selectedBusinessId');
  }, []);

  const clearCurrentBusiness = useCallback(() => {
    // Add a small delay to allow animations to play before clearing business state
    setTimeout(() => {
      setCurrentBusinessId(null);
      // Clear from localStorage when business is cleared
      sessionStorage.removeItem('selectedBusinessId');
    }, 600);
  }, []);

  const clearAndLogout = useCallback(async (): Promise<void> => {
    try {
      // Clear profile data first
      clearProfile();

      // Then trigger logout from AuthContext
      await logout();
    } catch (error) {
      console.error('Error during logout:', error instanceof Error ? error.message : error);
      // Even if logout fails, clear local state
      clearProfile();
    }
  }, [logout, clearProfile]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!isAuthenticated) {
      setProfileData(null);
      setCurrentBusinessId(null);
      setLoading(false);
      return;
    }

    try {
      const userProfile = await userService.getProfile();
      console.log('=== PROFILE FETCHED ===');
      console.log('User Profile:', userProfile);
      console.log('Corporate Entities:', userProfile.corporateEntities);

      setProfileData(userProfile);

      // Restore previously selected business from localStorage if valid
      const savedBusinessId = sessionStorage.getItem('selectedBusinessId');
      console.log('=== BUSINESS SELECTION LOGIC ===');
      console.log(
        'Available business IDs:',
        userProfile.corporateEntities?.map((ce) => ce.id) || [],
      );
      console.log('Saved business ID:', savedBusinessId);

      if (
        savedBusinessId &&
        userProfile.corporateEntities?.find((ce) => ce.id === savedBusinessId)
      ) {
        console.log('Restoring saved business selection:', savedBusinessId);
        setCurrentBusinessId(savedBusinessId);
      } else {
        console.log('No valid saved business - user must choose');
        setCurrentBusinessId(null);
        // Clear invalid saved business
        if (savedBusinessId) {
          sessionStorage.removeItem('selectedBusinessId');
        }
      }
      console.log('===============================');
    } catch (err) {
      console.error('Profile fetch error:', err instanceof Error ? err.message : err);
      setError('Failed to load profile');
      setProfileData(null);
      setCurrentBusinessId(null);

      // Assume session expired and log the user out
      await clearAndLogout();
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, clearAndLogout]);

  const contextValue = useMemo(
    () => ({
      profileData,
      loading,
      error,
      availableBusinesses,
      currentBusinessId,
      switchBusiness,
      clearCurrentBusiness,
      clearAndLogout,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      profileData,
      loading,
      error,
      availableBusinesses,
      currentBusinessId,
      switchBusiness,
      clearCurrentBusiness,
    ],
  );

  return <ProfileContext.Provider value={contextValue}>{children}</ProfileContext.Provider>;
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
